import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth, api } from '../context/AuthContext';
import { 
  Bell, 
  SignOut, 
  SquaresFour, 
  User, 
  Gear, 
  Users, 
  Buildings, 
  ChartLineUp, 
  CurrencyDollar,
  House,
  CaretDown,
  List,
  X,
  LinkedinLogo,
  ArrowRight,
  InstagramLogo,
  Receipt,
  MagnifyingGlass,
  Eye,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import AuthModal from './AuthModal';
import { cn } from '../lib/utils';
import { getAppReturnPath } from './app/AppShell';

const Header = () => {
  const { user, logout, showAuthModal, setShowAuthModal, authModalTab, openAuthModal, closeAuthModal } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [adminSearch, setAdminSearch] = useState('');
  const [searchResults, setSearchResults] = useState({ projects: [], parties: [] });
  const [searchLoading, setSearchLoading] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isAdminPage = location.pathname.startsWith('/admin');

  // Handle scroll effect
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    const isAdminUser = user.role === 'admin';
    const request = isAdminUser
      ? api.get('/admin/workspace/attention?limit=5')
      : api.get('/portfolio/v2/activity?limit=5');
    request
      .then((res) => {
        if (isAdminUser) {
          setNotifications((res.data.items || []).map((i) => ({
            title: i.reason,
            subtitle: [i.entity, i.project].filter(Boolean).join(' · '),
            route: i.action_route,
          })));
        } else {
          setNotifications((res.data.events || []).map((e) => ({
            title: e.title,
            subtitle: e.subtitle,
            route: '/activity',
          })));
        }
      })
      .catch(() => setNotifications([]));
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle logo click
  const handleLogoClick = (e) => {
    const targetPath = isAdmin ? '/admin' : '/';
    if (location.pathname === targetPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Admin navigation items - V2 only
  const adminNavItems = [
    { label: 'Dashboard', path: '/admin', icon: SquaresFour },
    { label: 'Parties', path: '/admin/v2/parties', icon: Users },
    { label: 'Projects', path: '/admin/v2/projects', icon: Buildings },
    { label: 'Subscriptions', path: '/admin/v2/subscriptions', icon: Receipt },
  ];

  // Check if nav item is active
  const isActiveNav = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  // Close mobile menu when navigating
  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    setAdminSearch('');
    setSearchResults({ projects: [], parties: [] });
    navigate(path);
  };

  // Admin search handler
  const handleAdminSearch = async (query) => {
    setAdminSearch(query);
    if (!query || query.length < 2) {
      setSearchResults({ projects: [], parties: [] });
      return;
    }
    
    setSearchLoading(true);
    try {
      const [projectsRes, partiesRes] = await Promise.all([
        api.get(`/admin/v2/projects?search=${encodeURIComponent(query)}&limit=5&include_drafts=true`),
        api.get(`/admin/v2/parties?search=${encodeURIComponent(query)}&limit=5`)
      ]);
      setSearchResults({
        projects: projectsRes.data.projects || [],
        parties: partiesRes.data.parties || []
      });
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <>
      <header 
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300",
          scrolled 
            ? "bg-slate-900 border-b border-white/10 shadow-lg" 
            : "bg-slate-900"
        )} 
        data-testid="main-header"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo - Left */}
            <Link 
              to={isAdmin ? '/admin' : '/'} 
              onClick={handleLogoClick} 
              className="flex items-center space-x-2 flex-shrink-0" 
              data-testid="logo-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_invest-portal-59/artifacts/c24cz0ev_Color%20logo%20-%20no%20background.png" 
                alt="Flippex Logo" 
                className="h-8 w-auto"
              />
            </Link>

            {/* Navigation - Center - Hidden on public pages (uses hamburger) */}
            {isAdmin && isAdminPage ? (
              /* Admin Navigation */
              <nav className="hidden lg:flex items-center space-x-1 absolute left-1/2 -translate-x-1/2" data-testid="admin-nav">
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors",
                        isActiveNav(item.path)
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-foreground-muted hover:text-foreground hover:bg-black/5"
                      )}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      <Icon size={18} weight={isActiveNav(item.path) ? "fill" : "regular"} />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            ) : null}

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {user && isAdmin ? (
                <>
                  {/* Notifications */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="relative p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="notifications-button">
                        <Bell size={20} weight="regular" className="text-white/70" />
                        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-white flex items-center justify-center">
                          {notifications.length}
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80 bg-white border-black/10 shadow-xl">
                      <div className="px-4 py-3 border-b border-black/5">
                        <p className="text-sm font-semibold">Notifications</p>
                      </div>
                      <div className="max-h-[300px] overflow-y-auto" data-testid="notifications-list">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-6 text-center">
                            <p className="text-sm text-foreground-muted">Nothing new right now.</p>
                          </div>
                        ) : (
                          notifications.map((n, i) => (
                            <div
                              key={i}
                              onClick={() => navigate(n.route)}
                              className="px-4 py-3 hover:bg-black/5 cursor-pointer border-b border-black/5 last:border-0"
                              data-testid="notification-item"
                            >
                              <p className="text-sm font-medium">{n.title}</p>
                              {n.subtitle && <p className="text-xs text-foreground-muted mt-0.5">{n.subtitle}</p>}
                            </div>
                          ))
                        )}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="user-menu-trigger">
                        <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </span>
                        </div>
                        <CaretDown size={16} className="text-white/70" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-white border-black/10 shadow-xl">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-foreground-muted">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator className="bg-black/5" />
                      
                      {isAdmin ? (
                        <>
                          <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin-dashboard">
                            <SquaresFour className="mr-2" size={18} />
                            Admin Dashboard
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/admin/v2/parties')} data-testid="menu-admin-parties">
                            <Users className="mr-2" size={18} />
                            Manage Parties
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/admin/v2/projects')} data-testid="menu-admin-projects">
                            <Buildings className="mr-2" size={18} />
                            Manage Projects
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-black/5" />
                          <DropdownMenuItem onClick={() => navigate('/')} data-testid="menu-home">
                            <House className="mr-2" size={18} />
                            View Site
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <>
                          <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                            <SquaresFour className="mr-2" size={18} />
                            Dashboard
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/portfolio')} data-testid="menu-portfolio">
                            <User className="mr-2" size={18} />
                            Portfolio
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                            <Gear className="mr-2" size={18} />
                            Settings
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      <DropdownMenuSeparator className="bg-black/5" />
                      <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                        <SignOut className="mr-2" size={18} />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  {/* Authenticated investors get a deliberate way back into the app */}
                  {user && (
                    <Link
                      to={getAppReturnPath()}
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-slate-900 text-sm font-medium hover:bg-white/90 transition-colors"
                      data-testid="my-flippex-button"
                    >
                      My Flippex
                      <ArrowRight size={14} weight="bold" />
                    </Link>
                  )}
                  {/* Hamburger Menu for all screen sizes (public pages) */}
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    aria-label="Open menu"
                    data-testid="menu-button"
                  >
                    <List size={24} className="text-white" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Full-Screen Slide-in Menu (works on all screen sizes) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 animate-in fade-in-0 duration-300"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Slide-in Panel from Right */}
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white animate-in slide-in-from-right duration-300">
            <div className="flex flex-col h-full">
              {/* Header with close button */}
              <div className="flex items-center justify-between px-6 h-16 border-b border-black/5">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_invest-portal-59/artifacts/c24cz0ev_Color%20logo%20-%20no%20background.png" 
                    alt="Flippex Logo" 
                    className="h-8 w-auto"
                  />
                </Link>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                  aria-label="Close menu"
                >
                  <X size={24} className="text-foreground" />
                </button>
              </div>

              {/* Menu Content */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                {/* Admin Navigation - shown when on admin pages */}
                {isAdminPage && isAdmin ? (
                  <>
                    {/* Admin Quick Search */}
                    <div className="mb-6">
                      <div className="relative">
                        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
                        <input
                          type="text"
                          value={adminSearch}
                          onChange={(e) => handleAdminSearch(e.target.value)}
                          placeholder="Search projects, parties..."
                          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-black/10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          data-testid="admin-quick-search"
                        />
                        {searchLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                      
                      {/* Search Results */}
                      {adminSearch.length >= 2 && (searchResults.projects.length > 0 || searchResults.parties.length > 0) && (
                        <div className="mt-2 bg-white border border-black/10 rounded-lg shadow-lg overflow-hidden">
                          {searchResults.projects.length > 0 && (
                            <div>
                              <p className="px-3 py-2 text-xs font-semibold text-foreground-muted bg-slate-50">Projects</p>
                              {searchResults.projects.map(project => (
                                <button
                                  key={project.id}
                                  onClick={() => handleNavClick(`/admin/v2/projects/${project.id}`)}
                                  className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2 border-b border-black/5 last:border-0"
                                >
                                  <Buildings size={16} className="text-primary flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{project.title}</p>
                                    <p className="text-xs text-foreground-muted">{project.code}</p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          {searchResults.parties.length > 0 && (
                            <div>
                              <p className="px-3 py-2 text-xs font-semibold text-foreground-muted bg-slate-50">Parties</p>
                              {searchResults.parties.map(party => (
                                <button
                                  key={party.id}
                                  onClick={() => handleNavClick(`/admin/parties`)}
                                  className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2 border-b border-black/5 last:border-0"
                                >
                                  <Users size={16} className="text-blue-600 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{party.display_name}</p>
                                    <p className="text-xs text-foreground-muted">{party.code} • {party.party_type}</p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {adminSearch.length >= 2 && !searchLoading && searchResults.projects.length === 0 && searchResults.parties.length === 0 && (
                        <p className="mt-2 text-sm text-foreground-muted text-center py-3">No results found</p>
                      )}
                    </div>

                    <nav className="space-y-4">
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Admin Modules</p>
                      <button 
                        onClick={() => handleNavClick('/admin')} 
                        className={cn(
                          "flex items-center gap-3 text-base font-medium transition-colors w-full text-left py-1",
                          location.pathname === '/admin' ? "text-primary" : "text-foreground hover:text-primary"
                        )}
                      >
                        <SquaresFour size={20} />
                        Dashboard
                      </button>
                      <button 
                        onClick={() => handleNavClick('/admin/v2/projects')} 
                        className={cn(
                          "flex items-center gap-3 text-base font-medium transition-colors w-full text-left py-1",
                          location.pathname.includes('/admin/v2/projects') || location.pathname.includes('/admin/projects') ? "text-primary" : "text-foreground hover:text-primary"
                        )}
                      >
                        <Buildings size={20} />
                        Projects
                      </button>
                      <button 
                        onClick={() => handleNavClick('/admin/parties')} 
                        className={cn(
                          "flex items-center gap-3 text-base font-medium transition-colors w-full text-left py-1",
                          location.pathname.includes('/admin/parties') ? "text-primary" : "text-foreground hover:text-primary"
                        )}
                      >
                        <Users size={20} />
                        Parties
                      </button>
                      <button 
                        onClick={() => handleNavClick('/admin/instruments')} 
                        className={cn(
                          "flex items-center gap-3 text-base font-medium transition-colors w-full text-left py-1",
                          location.pathname.includes('/admin/instruments') ? "text-primary" : "text-foreground hover:text-primary"
                        )}
                      >
                        <Receipt size={20} />
                        Instruments
                      </button>
                      <button 
                        onClick={() => handleNavClick('/admin/subscriptions')} 
                        className={cn(
                          "flex items-center gap-3 text-base font-medium transition-colors w-full text-left py-1",
                          location.pathname.includes('/admin/subscriptions') ? "text-primary" : "text-foreground hover:text-primary"
                        )}
                      >
                        <CurrencyDollar size={20} />
                        Subscriptions
                      </button>
                      
                      <div className="border-t border-black/10 pt-4 mt-4">
                        <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Quick Links</p>
                        <button 
                          onClick={() => handleNavClick('/projects')} 
                          className="flex items-center gap-3 text-base font-medium text-foreground hover:text-primary transition-colors w-full text-left py-1"
                        >
                          <Eye size={20} />
                          View Public Site
                        </button>
                      </div>
                    </nav>
                  </>
                ) : (
                  /* Public Navigation */
                  <nav className="space-y-5 mb-10">
                    <button 
                      onClick={() => handleNavClick('/projects')} 
                      className={cn(
                        "flex items-center gap-2 text-lg font-medium transition-colors",
                        location.pathname.startsWith('/projects') ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      Projects
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-green-500/20 text-green-500 animate-pulse">LIVE</span>
                    </button>
                    <button 
                      onClick={() => handleNavClick('/marketplace')} 
                      className={cn(
                        "flex items-center gap-2 text-lg font-medium transition-colors",
                        location.pathname === '/marketplace' ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      Marketplace
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary">SOON</span>
                    </button>
                    <button 
                      onClick={() => handleNavClick('/how-it-works')} 
                      className={cn(
                        "block text-lg font-medium transition-colors",
                        location.pathname === '/how-it-works' ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      How It Works
                    </button>
                    <button 
                      onClick={() => handleNavClick('/submit-project')} 
                      className={cn(
                        "block text-lg font-medium transition-colors",
                        location.pathname === '/submit-project' ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      For Developers
                    </button>
                    <button 
                      onClick={() => handleNavClick('/about')} 
                      className={cn(
                        "block text-lg font-medium transition-colors",
                        location.pathname === '/about' ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      About Flippex
                    </button>
                    <button 
                      onClick={() => handleNavClick('/news')} 
                      className={cn(
                        "block text-lg font-medium transition-colors",
                        location.pathname.startsWith('/news') ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      News & Stories
                    </button>
                    <button 
                      onClick={() => handleNavClick('/faqs')} 
                      className={cn(
                        "block text-lg font-medium transition-colors",
                        location.pathname === '/faqs' ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      FAQs
                    </button>
                    <button 
                      onClick={() => handleNavClick('/contact')} 
                      className={cn(
                        "block text-lg font-medium transition-colors",
                        location.pathname === '/contact' ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      Contact
                    </button>
                    <button 
                      onClick={() => handleNavClick('/legal')} 
                      className={cn(
                        "block text-lg font-medium transition-colors",
                        location.pathname === '/legal' ? "text-primary" : "text-foreground hover:text-primary"
                      )}
                    >
                      Legal
                    </button>
                  </nav>
                )}

                {/* Social Links - only on public pages */}
                {!isAdminPage && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wider mb-4">Follow Us</h3>
                    <div className="flex space-x-4">
                      <a href="https://www.linkedin.com/company/flippex/" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors">
                        <LinkedinLogo size={24} weight="regular" />
                      </a>
                      <a href="#" className="text-foreground hover:text-primary transition-colors">
                        <InstagramLogo size={24} weight="regular" />
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom CTA - different for admin vs public */}
              <div className="px-6 py-6 border-t border-black/5">
                {isAdminPage && isAdmin ? (
                  <Button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      logout();
                      navigate('/');
                    }}
                    variant="outline"
                    className="w-full rounded-full py-6 text-lg font-medium"
                  >
                    <SignOut size={20} className="mr-2" />
                    Sign Out
                  </Button>
                ) : user ? (
                  <>
                    <Button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate(getAppReturnPath());
                      }}
                      className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-lg font-medium"
                      data-testid="menu-my-flippex"
                    >
                      My Flippex
                      <ArrowRight size={18} weight="bold" className="ml-2" />
                    </Button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        logout();
                      }}
                      className="w-full mt-3 text-center text-foreground-muted hover:text-foreground transition-colors"
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        openAuthModal('register');
                      }}
                      className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 text-lg font-medium"
                    >
                      Get Started
                    </Button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        openAuthModal('login');
                      }}
                      className="w-full mt-3 text-center text-foreground-muted hover:text-foreground transition-colors"
                    >
                      Already have an account? <span className="text-primary font-medium">Sign in</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AuthModal open={showAuthModal} onClose={closeAuthModal} defaultTab={authModalTab} />
    </>
  );
};

export default Header;
